import * as summaryApi from "app/api/summaryApi";
import { FreshworksHelper } from "app/helpers/freshworksHelper";
import { PromoCategoryHelper } from "app/helpers/promoCategoryHelper";
import { RowHeightHelper } from "app/helpers/rowHeightHelper";
import SummaryLogic from "app/logic/summaryLogic";
import LandingPagePromo from "app/screens/PublicScreen/components/Promo/LandingPagePromo";
import type { CompetitionType } from "app/types/competitionType";
import type { EventType } from "app/types/eventType";
import { PromoType } from "app/types/promoType";
import type { RouterMatchType } from "app/types/types";
import type { UserType } from "app/types/userType";
import {
  competitionListTimeEnum,
  ListRenderValues,
  ResponsiveScreenSizes,
  TabTypes,
} from "app/utils/const";
import isPublicView from "app/utils/publicViewUtils";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import React, { Component, lazy, Suspense } from "react";
import { FormattedMessage } from "react-intl";
import Media from "react-media";
import { connect } from "react-redux";
import { VariableSizeList as List } from "react-window";
import { $Values } from "utility-types";
import CompetitionCell from "../../components/CompetitionCell/CompetitionCell";
import "./CompetitionsScreen.scss";
import UnsummarizedMarketsScreen from "./components/UnsummarizedMarketsScreen/UnsummarizedMarketsScreen";
// test
const LoadingSpinner: React.LazyExoticComponent<any> = lazy(
  () => import("app/components/LoadingSpinner/LoadingSpinner")
);
const EventsScreenMarketsHeader: React.LazyExoticComponent<any> = lazy(
  () =>
    import(
      "../../components/EventScreenMarketsHeader/EventsScreenMarketsHeader"
    )
);
const EventsScreenTimeFilter: React.LazyExoticComponent<any> = lazy(
  () => import("../../components/EventsScreenTimeFilter/EventsScreenTimeFilter")
);
const EventsScreenHeader: React.LazyExoticComponent<any> = lazy(
  () => import("../../components/EventsScreenHeader/EventsScreenHeader")
);

type Props = {
  match: RouterMatchType;
  competitionList: CompetitionType[];
  competitionListTotal: number;
  isCompetitionListLoading: boolean;
  setCompetitionListLoading: boolean;
  competitionListNumberLiveEvents: number;
  setSingleCompetition: (...args: Array<any>) => any;
  updateUnsummarizedMarketsModalEvent: (...args: Array<any>) => any;
  resetCompetitions: (...args: Array<any>) => any;
  resetFiltering: (...args: Array<any>) => any;
  filterNavNodeIds: string;
  isIdle: boolean;
  user: UserType;
  isUserLoading: boolean;
  selectedCompetitionType: $Values<typeof TabTypes>;
  updateCompetitionListType: (...args: Array<any>) => any;
  updateCompetitionListTime: (...args: Array<any>) => any;
  setBookmarksOnly: (...args: Array<any>) => any;
  loadFavouritesCount: (...args: Array<any>) => any;
  disableFilterTab: boolean;
  disableFilter: (...args: Array<any>) => any;
  disableTodayEarlyFilter: (...args: Array<any>) => any;
  bookmarksTotal: number;
  setCompetitionWithFavouritesList: (...args: Array<any>) => any;
  setFavouritesOnly: (...args: Array<any>) => any;
  loadFavourites: (...args: Array<any>) => any;
  currentFavouritesCount: number;
  setFutures: Function;
  navigationMenuOpen: boolean;
  checkIfMenuOpen: (...args: Array<any>) => any;
  favouritesOnly: boolean;
  bookmarksOnly: boolean;
  singleCompetitionOnly: boolean;
  time: string;
  history: any;
  setPositionMatrixModalEvent: (...args: Array<any>) => any;
  setPositionMatrixModalItem: (...args: Array<any>) => any;
  openPositionMatrixModal: (...args: Array<any>) => any;
  isPageVisible: boolean;
  unsummarizedMarketsModalEvent: EventType;
  fullListLoaded: boolean;
  defaultSportId: number;
  futures: boolean;
  updateSportsSelection: Function;
  sportsSelectionList: any[];
  navigationTree: any[];
  updateBreadCrumb: Function;
  unsummarizedMarketsModalEventLoading: boolean;
  promoCategory: PromoType;
  updatePromoCompetitionLoading: Function;
  updatePromoSportLoading: Function;
  resetExpandedCompetitions: Function;
};
type State = {
  time: any;
  favouritesActive: boolean;
  positionMatrixOpen: boolean;
  positionMatrixEvent: any;
  line: any;
  hasInitiallyLoaded: boolean;
};

class CompetitionsScreen extends Component<Props, State> {
  list: any;
  isUserLoaded: boolean;
  accountStatsIntervalId: any;
  favouritesLoading: boolean;

  constructor(props: Props) {
    super(props);
    this.state = {
      time: this.props.time,
      favouritesActive: this.props.favouritesOnly,
      positionMatrixOpen: false,
      positionMatrixEvent: null,
      line: "",
      hasInitiallyLoaded: false,
    };
    this.list = React.createRef();
    this.isUserLoaded = false;
    // Tracking favourites loading state separately
    // The default loading state logic for other list types is incompatible
    this.favouritesLoading = false;
    this.props.resetExpandedCompetitions();
    this.startAccountStatsInterval();
    this.props.loadFavouritesCount();
    !isPublicView() && FreshworksHelper.hideWidget();
  }

  componentDidMount() {
    // When navigating away and then back to favourites,
    // eg favs then go to reports then back to favs, need to reload the list of favourites.
    // So if there are favourited events, and the competition list is empty then reload favourites
    if (
      this.props.favouritesOnly &&
      !this.favouritesLoading &&
      this.props.currentFavouritesCount > 0 &&
      this.props.competitionList &&
      this.props.competitionList.length === 0
    ) {
      this.favouritesLoading = true;
      this.props.loadFavourites(() => {
        this.props.setCompetitionWithFavouritesList({
          showMarketsGroupedByPeriodAndType: true,
        });
        this.favouritesLoading = false;
      });
    }
    // get a list of sports with number of events in each sport
    // Used for checking if there are events TODAY for a given sport
    this.props.updateSportsSelection();
  }

  componentDidUpdate(prevProps, prevState) {
    const { isCompetitionListLoading, competitionList, promoCategory } =
      this.props;

    // To handle No Events content showing when list hasnt yet been fetched.
    // Don't show No Events content until hasInitiallyLoaded is true
    // The following check is true if the list was previously loading and is now not loading,
    // ie the list has been fetched.
    if (prevProps.isCompetitionListLoading && !isCompetitionListLoading) {
      this.setState({
        hasInitiallyLoaded: true,
      });
    }
    //

    !isPublicView() && FreshworksHelper.hideWidget();
    // if promoted event load select event
    if (!isEmpty(promoCategory)) {
      PromoCategoryHelper.updatePromotedCategories(
        promoCategory,
        this.props,
        prevProps,
        false
      );
    }

    // If the competition list has been updated with new items
    // which means that the loading is done
    if (!isCompetitionListLoading && prevProps.isCompetitionListLoading) {
      // Then clear the calculated height of the list
      this.resizeCell(0);
    }

    // Clear cache of list if any events in a competition has been removed
    // First check if the competition list had items in it before the update
    if (
      prevProps.competitionList != null &&
      prevProps.competitionList.length > 0
    ) {
      prevProps.competitionList.every(prevCompetition => {
        // find the same competition in current list of competitions
        const newCompetitionIndex = competitionList.findIndex(
          competition => competition.id === prevCompetition.id
        );
        const newCompetition = competitionList[newCompetitionIndex];
        // if same competition is found
        // and its number of shown events (not the total number of events)
        // is different from the previous competition
        if (
          newCompetition != null &&
          newCompetition.events.length !== prevCompetition.events.length
        ) {
          // then clear calculated height of the competition cell
          setTimeout(() => {
            this.resizeCell(newCompetitionIndex);
          }, 0);
          return false; // break loop
        }

        return true; // continue loop
      });
    }
  }

  componentWillUnmount() {
    this.props.resetCompetitions();
    this.clearAccountStatsInterval();
    this.props.updateUnsummarizedMarketsModalEvent(null);
  }

  handleChangeListTime = (time: string) => {
    // time can be EARLY or TODAY
    if (this.props.competitionList.length > 0) this.props.resetCompetitions();
    this.props.updateCompetitionListTime(time);
    this.setState({
      time: time,
    });
  };

  getSelectedSport = () => {
    // filterNavNodeIds is a string some places, and an array others ?????
    // so
    if (typeof this.props.filterNavNodeIds === "object") {
      return false;
    } else if (typeof this.props.filterNavNodeIds === "string") {
      return this.props.filterNavNodeIds.split(",")[0];
    }
  };

  handleChangeListType = (typeChange: string) => {
    // typeChange can be LIVE, POPULAR, ALL
    this.props.resetExpandedCompetitions();
    // update store competition type with change
    this.props.updateCompetitionListType(typeChange);

    if (this.props.competitionList.length > 0) {
      this.props.resetCompetitions();
    }
  };

  startAccountStatsInterval() {
    // Poll account stats as bookmarks get removed if events are removed
    // Too much processing server-side to send websockets
    this.accountStatsIntervalId = setInterval(() => {
      if (!this.props.isIdle) {
        summaryApi.fetchSummary().then(resp => {
          SummaryLogic.updateSummary(resp.accountStats);
        });
      }
    }, 20000);
  }

  clearAccountStatsInterval() {
    clearInterval(this.accountStatsIntervalId);
  }

  renderEndOfList = () => {
    return (
      <div className="competitions-screen__list__end-of-list">
        {this.props.fullListLoaded ||
        this.props.competitionList.length < ListRenderValues.THRESHOLD ? (
          <FormattedMessage tagName="span" id="events-nomore" />
        ) : (
          <LoadingSpinner />
        )}
      </div>
    );
  };
  rowRenderer = ({ index, style }) => {
    let content;
    const competition: CompetitionType = this.props.competitionList[index];

    if (competition != null) {
      content = (
        <CompetitionCell
          data={competition}
          resizeCell={this.resizeCell}
          competitionIndex={index}
          key={competition.id}
          setCompetitionWithFavouritesList={
            this.props.setCompetitionWithFavouritesList
          }
          line={this.state.line}
        />
      );
    } else {
      // this is used as a placeholder for the very last item
      // in the list to add some bottom "padding"
      content = (
        <React.Fragment>
          <div className="competitions-screen__list__bottom-spacer" />
          {/* BET-10860 AC4: Remove the “no more events” message that is currently displayed at the bottom of the popular page */}
          {/* {this.props.competitionList.length > 0 && this.renderEndOfList()} */}
        </React.Fragment>
      );
    }

    return (
      <div key={index} style={style}>
        {content}
      </div>
    );
  };
  onClickFutures = (futures: boolean) => {
    if (futures) {
      this.props.history.push("/futures/");
    } else {
      this.props.history.goBack();
    }
  };
  onClickBookmarks = () => {
    if (!this.props.bookmarksOnly) {
      this.props.history.push("/bookmarks/");
    } else {
      this.props.history.goBack();
    }
  };
  resetFiltering = () => {
    this.props.resetFiltering();
    this.props.disableFilter(false);
    this.props.disableTodayEarlyFilter(false);
  };
  onClickFavourites = () => {
    this.setState({
      favouritesActive: !this.state.favouritesActive,
    });
    if (!this.props.favouritesOnly) {
      this.props.history.push("/favourites/");
    } else {
      this.props.history.goBack();
    }
  };
  // clear the cache of the cell and the list
  resizeCell = (index: number) => {
    if (this.list && this.list.current) {
      this.list.current.resetAfterIndex(index);
    }
  };
  shouldComponentUpdate = (nextProps, nextState) => {
    // if favourites view selected after competitionlist reset
    // clear cache for all rows to recalculate each event row height again
    if (nextState.favouritesActive && nextProps.competitionList.length > 0) {
      this.resizeCell(0);
    }

    return true;
  };
  checkClickEvent = (e: any) => {
    if (this.props.navigationMenuOpen) {
      this.props.checkIfMenuOpen(false);
    }
  };
  closePositionMatrix = () => {
    this.setState({
      positionMatrixOpen: false,
    });
  };
  getNoEventsContent = () => {
    const {
      selectedCompetitionType,
      bookmarksTotal,
      bookmarksOnly,
      favouritesOnly,
    } = this.props;
    let message = (
      <FormattedMessage tagName="span" id="events-no-active-events" />
    );
    // no live events
    if (
      !favouritesOnly &&
      !bookmarksOnly &&
      selectedCompetitionType === TabTypes.LIVE
    ) {
      message = <FormattedMessage tagName="span" id="events-no-live-events" />; // No events today
    } else if (
      !favouritesOnly &&
      !bookmarksOnly &&
      this.state.time === competitionListTimeEnum.TODAY &&
      selectedCompetitionType === TabTypes.ALL
    ) {
      message = <FormattedMessage tagName="span" id="events-no-events-today" />; // No active events
    } else if (
      !favouritesOnly &&
      !bookmarksOnly &&
      this.state.time === competitionListTimeEnum.EARLY &&
      selectedCompetitionType === TabTypes.ALL
    ) {
      message = (
        <FormattedMessage tagName="span" id="events-no-active-events" />
      ); // no event for next 24 hours
    } else if (
      !favouritesOnly &&
      !bookmarksOnly &&
      selectedCompetitionType === TabTypes.POPULAR
    ) {
      message = (
        <FormattedMessage tagName="span" id="events-no-events-24-hours" />
      ); // no favourites
    } else if (!this.props.bookmarksOnly && favouritesOnly) {
      message = <FormattedMessage tagName="span" id="favourites-no-events" />; // no bookmarked leagues have events
    } else if (!favouritesOnly && bookmarksOnly && bookmarksTotal !== 0) {
      message = (
        <FormattedMessage tagName="span" id="events-no-active-events" />
      ); // no  leagues bookmarked
    } else if (!favouritesOnly && bookmarksOnly && bookmarksTotal === 0) {
      message = <FormattedMessage tagName="span" id="events-no-bookmarks" />;
    }
    //
    return (
      <div className="competitions-screen__list__empty-list">{message}</div>
    );
  };

  calculateListHeight = () => {
    let height = 1000;

    if (window.innerHeight > 200) {
      height = window.innerHeight - 150;
    }

    return height;
  };

  render() {
    const {
      selectedCompetitionType,
      disableFilterTab,
      competitionList,
      competitionListNumberLiveEvents,
      setCompetitionListLoading,
      isCompetitionListLoading,
      history,
      unsummarizedMarketsModalEvent,
      futures,
    } = this.props;

    const listCount = this.props.competitionList.length + 1;
    const competitionType =
      selectedCompetitionType && selectedCompetitionType.toLowerCase();
    let content = <div />;
    // Check that there is a list to display, otherwise show blank state message

    const path = this.props.history.location.pathname;
    // FIX: we check if the single event is set in the store to know whether to show it or not
    // should be a better way to handle this using the  router
    // but would need to refactor a lot
    // so for now, only show the single event when the route is /event/
    if (!isEmpty(unsummarizedMarketsModalEvent) && path.startsWith("/event/")) {
      content = <UnsummarizedMarketsScreen />;
    } else {
      if (
        competitionList.length === 0 &&
        !isCompetitionListLoading &&
        !setCompetitionListLoading &&
        !this.favouritesLoading &&
        this.state.hasInitiallyLoaded
      ) {
        content = this.getNoEventsContent();
      } else if (competitionList.length > 0) {
        content = (
          <div
            className="competitions-screen__wrapper"
            onClick={() => this.checkClickEvent()}
          >
            <List
              ref={this.list}
              className="List"
              itemCount={listCount}
              height={this.calculateListHeight()}
              itemSize={RowHeightHelper.calculateEuroRowHeight}
              itemData={this.props.competitionList}
            >
              {this.rowRenderer}
            </List>
          </div>
        );
      } else {
        content = (
          <Suspense fallback={<div className="fallback"></div>}>
            <LoadingSpinner />
          </Suspense>
        );
      }
    }

    return (
      <div className="competitions-screen">
        <Suspense fallback={<div className="fallback">loading...</div>}>
          <EventsScreenHeader
            competitionListNumberLiveEvents={competitionListNumberLiveEvents}
            callback={this.handleChangeListType}
            onClickFutures={this.onClickFutures}
            onClickBookmarks={this.onClickBookmarks}
            onClickFavourites={this.onClickFavourites}
            bookmarksActive={this.props.bookmarksOnly}
            type={selectedCompetitionType}
            disableFilterTab={disableFilterTab}
            currentFavouritesCount={this.props.currentFavouritesCount}
            isCompetitionListLoading={this.props.isCompetitionListLoading}
            favouritesActive={this.state.favouritesActive}
            checkClickEvent={this.checkClickEvent}
            history={history}
          />
        </Suspense>
        {/* desktop only component */}
        {isEmpty(unsummarizedMarketsModalEvent) && (
          <>
            {isPublicView() && <LandingPagePromo />}
            <div
              className={`events-screen__sub-header ${competitionType}`}
              onClick={() => this.checkClickEvent()}
            >
              {!futures &&
                !this.props.bookmarksOnly &&
                !this.props.favouritesOnly &&
                !this.props.singleCompetitionOnly &&
                selectedCompetitionType === TabTypes.ALL && (
                  <Suspense
                    fallback={<div className="fallback">loading...</div>}
                  >
                    <EventsScreenTimeFilter
                      time={this.state.time}
                      callback={this.handleChangeListTime}
                      favouritesActive={this.props.favouritesOnly}
                    />
                  </Suspense>
                )}
              {!futures && this.props.competitionList.length > 0 && (
                <Media
                  query={`(min-width: ${ResponsiveScreenSizes.desktop})`}
                  render={() => (
                    <Suspense
                      fallback={<div className="fallback">loading...</div>}
                    >
                      <EventsScreenMarketsHeader data={this.props} />
                    </Suspense>
                  )}
                />
              )}
            </div>
          </>
        )}
        {content}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  competitionList: state.competitionListModel.competitionList,
  competitionListTotal: state.competitionListModel.competitionListTotal,
  isCompetitionListLoading: state.competitionListModel.isCompetitionListLoading,
  setCompetitionListLoading:
    state.competitionListModel.setCompetitionListLoading,
  competitionListNumberLiveEvents:
    state.competitionListModel.competitionListNumberLiveEvents,
  filterNavNodeIds: state.navigationModel.filterNavNodeIds,
  user: state.userModel.user,
  isUserLoading: state.userModel.isUserLoading,
  selectedCompetitionType: state.competitionsListFiltersModel.type,
  time: state.competitionsListFiltersModel.time,
  disableFilterTab: state.competitionListModel.disableFilterTab,
  bookmarksTotal: state.bookmarkListModel.total,
  currentFavouritesCount: state.favouritesListModel.currentFavouritesCount,
  navigationMenuOpen: state.navigationModel.navigationMenuOpen,
  favouritesOnly: state.competitionListModel.favouritesOnly,
  bookmarksOnly: state.competitionsListFiltersModel.bookmarksOnly,
  singleCompetitionOnly: state.competitionListModel.singleCompetitionOnly,
  isIdle: state.sessionModel.isIdle,
  isPageVisible: state.sessionModel.isPageVisible,
  unsummarizedMarketsModalEvent:
    state.competitionListModel.unsummarizedMarketsModalEvent,
  fullListLoaded: state.competitionListModel.fullListLoaded,
  futures: state.competitionListModel.futures,
  sportsSelectionList: state.sportsSelectionListModel.sportsSelectionList,
  navigationTree: state.navigationModel.navigationTree,
  promoCategory: state.promoCategoryModel,
});

const mapDispatchToProps = (dispatch: any) => ({
  setSingleCompetition: () => {
    dispatch.competitionListModel.setSingleCompetitionList();
  },
  updateUnsummarizedMarketsModalEvent: (event: EventType) =>
    dispatch.competitionListModel.updateUnsummarizedMarketsModalEvent(event),
  resetCompetitions: () => {
    dispatch.competitionListModel.resetCompetition();
  },
  resetFiltering: () => {
    dispatch.navigationModel.resetFiltering();
  },
  updateCompetitionListType: (type: string) => {
    dispatch.competitionsListFiltersModel.updateCompetitionListType(type);
  },
  updateCompetitionListTime: time => {
    dispatch.competitionsListFiltersModel.updateCompetitionListTime(time);
  },
  setBookmarksOnly: (bookmarksOnly: boolean) => {
    dispatch.competitionsListFiltersModel.setBookmarksOnly(bookmarksOnly);
  },
  setFavouritesOnly: (favouritesOnly: boolean) => {
    dispatch.competitionListModel.setFavouritesOnly(favouritesOnly);
  },
  loadFavourites: callback => {
    dispatch.favouritesListModel.setFavouriteList({
      callback,
    });
  },
  loadFavouritesCount: () => {
    dispatch.favouritesListModel.getFavouriteListCount();
  },
  disableFilter: disable => {
    dispatch.competitionListModel.disableFilter(disable);
  },
  setCompetitionWithFavouritesList: callback => {
    dispatch.competitionListModel.setCompetitionWithFavouritesList(callback);
  },
  checkIfMenuOpen: value => dispatch.navigationModel.checkIfMenuOpen(value),
  disableTodayEarlyFilter: disable => {
    dispatch.competitionListModel.disableTodayEarlyFilter(disable);
  },
  setFutures: (futures: boolean) => {
    dispatch.competitionListModel.setFutures(futures);
  },
  updateBreadCrumb: updateBreabcrumbState =>
    dispatch.navigationModel.updateBreadCrumb(updateBreabcrumbState),
  updateSportsSelection: (after, before) =>
    dispatch.sportsSelectionListModel.updateSportsSelection({
      after: dayjs().endOf("day").add(4.5, "hours").unix(),
      before: dayjs().endOf("day").add(4.5, "hours").unix(),
    }),
  updatePromoCompetitionLoading: (status: boolean) => {
    dispatch.promoCategoryModel.updatePromoCompetitionLoading(status);
  },
  updatePromoSportLoading: (status: boolean) => {
    dispatch.promoCategoryModel.updatePromoSportLoading(status);
  },
  resetExpandedCompetitions: () =>
    dispatch.competitionsListFiltersModel.resetExpandedCompetitions(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CompetitionsScreen) as any;
